<template>
  <modal-card
    title="Snippet"
    fixed
    full
    padding
    class="search-modal"
    @close="$emit('close')"
  >
    <div v-if="showSpinner" class="spinner-wrap pa-5">
      <spinner />
    </div>

    <template v-else>
      <v-text-field
        placeholder="Search"
        class="search-input"
        prepend-icon="search"
        v-model="search"
        clearable
        clear-icon="cancel"
      />

      <options-list :options="filteredSnippets" @select="handleSnippetSelect" />
    </template>
  </modal-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { compareStrings, highlightSearch } from '@/filters/helpers';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';

import OptionsList from './OptionsList';

const EXACT_MATCH_FIELD_TYPES = [
  'string',
  'bool',
  'time',
  'email',
  'textarea',
  'int',
];

export default {
  name: 'HtmlSnippets',

  components: {
    OptionsList,
  },

  props: {
    sms: Boolean,
    contactId: String,
    insideQuill: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: 'Snippet',
    },
  },

  data() {
    return {
      search: '',
      contact: null,
      compiling: false,
    };
  },

  computed: {
    ...mapGetters('snippetsFolders', ['loading', 'all']),
    ...mapGetters('vector', ['outreachSnippets']),

    showSpinner() {
      return this.loading || this.compiling;
    },

    contactFields() {
      if (!this.contact) return null;

      return Object.values(this.contact).find((item) => item.type === 'default')
        ?.fields;
    },

    filteredGroups() {
      if (!this.contact) return this.all;

      return this.all.filter((folder) => {
        const { contact_conditions, contact_conditions_type } = folder || {};

        if (!contact_conditions?.length) return true;

        return this.checkConditions(
          contact_conditions,
          contact_conditions_type
        );
      });
    },

    filteredSnippets() {
      const snippets = [];

      // Add vector snippets first
      if (this.isVectorRecruiting) {
        const vectorGroup = this.buildVectorSnippetsGroup();
        if (vectorGroup) snippets.push(vectorGroup);
      }

      // Add regular snippet folders
      const regularGroups = this.buildRegularSnippetsGroups();
      snippets.push(...regularGroups);

      return snippets;
    },
  },

  methods: {
    ...mapActions(['loadSingleContact', 'compileTextForContact']),
    ...mapActions('snippetsFolders', ['load']),
    ...mapActions('vector', ['loadVectorSnippets']),

    // Snippet filtering and building
    buildVectorSnippetsGroup() {
      if (!this.outreachSnippets?.length) return null;

      const filtered = this.filterAndFormatSnippets(this.outreachSnippets);

      return filtered.length ? { name: 'Default', options: filtered } : null;
    },

    buildRegularSnippetsGroups() {
      return cloneDeep(this.filteredGroups || [])
        .sort((a, b) => a.order - b.order)
        .reduce((groups, folder) => {
          const snippets = this.filterAndFormatSnippets(
            (folder.snippets || []).sort((a, b) => a.order - b.order)
          );

          if (snippets.length) {
            groups.push({ name: folder.name, options: snippets });
          }

          return groups;
        }, []);
    },

    filterAndFormatSnippets(snippets) {
      return snippets
        .filter((snippet) => this.shouldIncludeSnippet(snippet))
        .map((snippet) => this.formatSnippet(snippet));
    },

    shouldIncludeSnippet(snippet) {
      const matchesSearch = compareStrings(snippet.name, this.search || '');
      const hasTextContent = !this.sms || snippet.text_content;

      return matchesSearch && hasTextContent;
    },

    formatSnippet(snippet) {
      return {
        ...clone(snippet),
        text: this.search
          ? highlightSearch(snippet.name, this.search)
          : snippet.name,
      };
    },

    // Contact condition checking
    checkConditions(conditions, type = 'all') {
      const results = conditions.map((condition) => this.checkField(condition));

      if (type === 'all') return results.every((result) => result === true);
      if (type === 'none') return !results.some((result) => result === true);
      if (type === 'any') return results.some((result) => result === true);

      return false;
    },

    checkField({ field, value }) {
      const contactValue = this.contactFields?.[field.field]?.value;

      if (EXACT_MATCH_FIELD_TYPES.includes(field.type)) {
        return value == contactValue;
      }

      if (field.type === 'list') {
        return (contactValue || '').includes(value);
      }

      return false;
    },

    // Text compilation
    async compileSnippetText(text) {
      if (!this.contactId || !text) return text;

      this.compiling = true;

      try {
        const compiledText = await this.compileTextForContact({
          contactId: this.contactId,
          content: text,
        });
        return compiledText || text;
      } finally {
        this.compiling = false;
      }
    },

    async prepareSnippetText(snippet) {
      const { text_content, content } = snippet || {};
      let text = content || text_content;

      if (this.sms) {
        text = await this.compileSnippetText(text_content);
        // Convert line breaks to br elements for quill

        if (this.insideQuill) {
          text = text.replace(/\s+\n\s+\n/, '\n\n').replace(/\n/g, '<br>');
        }
      }

      return text;
    },

    // Event handlers
    async handleSnippetSelect(snippet) {
      try {
        const text = await this.prepareSnippetText(snippet);

        this.$emit('insert', text);
        this.$emit('snippetUsed', {
          snippet_id: snippet.id,
          snippet_shortcut: snippet.shortcut,
          snippet_files: snippet.files,
        });
      } finally {
        this.$emit('close');
      }
    },

    // Initialization
    async loadContact() {
      if (!this.contactId) return;

      this.contact = await this.loadSingleContact(this.contactId);
    },

    initializeData() {
      if (!this.all.length) {
        this.load();
      }

      if (this.isVectorRecruiting) {
        this.loadVectorSnippets();
      }

      this.loadContact();
    },
  },

  mounted() {
    this.initializeData();
  },
};
</script>
